/**
 * State layer — a tiny vanilla store (no dependency) exposed to React through
 * useSyncExternalStore. UI components never mutate state directly; they call
 * actions defined here, which then request a debounced autosave.
 *
 * AppState = Campaign State (user data) + UI State (ephemeral).
 * Campaign edits are recorded in ./history.ts (bounded undo/redo); UI state
 * never enters the history, and REPLACING the document resets it.
 */
import { useSyncExternalStore } from 'react';
import type {
  AdvancementEntry,
  AdvancementState,
  Campaign,
  Character,
  CharacterSheet,
  DnD5eSheet,
  Encounter,
  Enemy,
  Faction,
  FateSheet,
  GameMap,
  Location,
  MapMarker,
  MapToken,
  Note,
  Npc,
  RuleSystemId,
  Session,
  StoryNode,
} from '@/domain/campaign';
import type { Refusal, FateMinorOption, MajorStepRequest, AdvancementOutcome } from '@/domain/advance';
import { createEntity, newId, nowIso, type Locale } from '@/domain/shared';
import { defaultMixedSheet } from '@/domain/mixed-sheet-factory';
import type { Hp } from '@/domain/hp';
import {
  recordCampaignEdit,
  resetCampaignHistory,
  takeRedo,
  takeUndo,
} from '@/state/history';

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';
/** {placeholders} for a toast message — e.g. the offending import field. */
export type ToastParams = Record<string, string | number>;
/** GM cockpit view ids — also the hash segments: `#/gmscreen`, `#/party`, … */
export type RouteId =
  | 'cockpit'
  | 'sessions'
  | 'characters'
  | 'party'
  | 'npcs'
  | 'forge'
  | 'notes'
  | 'encounters'
  | 'world'
  | 'mapforge'
  | 'story'
  | 'content'
  | 'rules'
  | 'reference'
  | 'gmscreen';

/**
 * Player-surface route ids — the hash segments under `#/play…`
 * (`#/play`, `#/play/rules`, `#/play/create`, …). They are routes like any
 * other: `ui.route` stays the ONE navigation state (see ui/router.ts), and
 * which SHELL renders them (GM chrome vs player shell) is derived from the
 * id, never stored twice.
 */
export type PlayerRouteId =
  | 'play'
  | 'play/rules'
  | 'play/content'
  | 'play/create'
  | 'play/characters';

/** Every routable view: GM cockpit ids + player-surface ids. */
export type AppRoute = RouteId | PlayerRouteId;

/** The default view behind `#/` and behind any unknown/empty hash. */
export const DEFAULT_ROUTE: RouteId = 'cockpit';

export interface AppState {
  campaign: Campaign | null;
  ui: {
    locale: Locale;
    /** Current view — GM or player. Single source of truth for the hash. */
    route: AppRoute;
    saveStatus: SaveStatus;
    lastSavedAt: string | null;
    /** i18n key of the last import/export error, if any. */
    toastKey: string | null;
    /** Parameters interpolated into the toast's {placeholders}. */
    toastParams: ToastParams | null;
    /** Ctrl+K palette visibility. */
    searchOpen: boolean;
    /** Entity selected in the current section's list pane (Phase 2+). */
    selectedId: string | null;
  };
}

const initialState: AppState = {
  campaign: null,
  ui: {
    locale: 'tr',
    route: 'cockpit',
    saveStatus: 'idle',
    lastSavedAt: null,
    toastKey: null,
    toastParams: null,
    searchOpen: false,
    selectedId: null,
  },
};

type Listener = () => void;

let state: AppState = initialState;
const listeners = new Set<Listener>();

function setState(patch: Partial<AppState> | ((s: AppState) => Partial<AppState>)): void {
  const partial = typeof patch === 'function' ? patch(state) : patch;
  state = { ...state, ...partial };
  for (const l of listeners) l();
}

export function setUiState(patch: Partial<AppState['ui']>): void {
  setState((s) => ({ ui: { ...s.ui, ...patch } }));
}

/** Non-reactive read of the whole state (tests/tools; UI uses hooks). */
export function getState(): AppState {
  return state;
}

// ------------------------------------------------------------------ actions

export function createCampaign(name: string, ruleSystem: RuleSystemId): Campaign {
  const campaign = createEntity({
    name,
    ruleSystem,
    era: null,
    synopsis: '',
    sessions: [],
    characters: [],
    npcs: [],
    enemies: [],
    encounters: [],
    locations: [],
    maps: [],
    factions: [],
    items: [],
    storyNodes: [],
    notes: [],
    pinnedGmPanels: [],
  });
  // A NEW document replaces the old one — history resets, so the GM can
  // never undo back into a previous campaign (see state/history.ts).
  resetCampaignHistory();
  setState({ campaign });
  return campaign;
}

export function replaceCampaign(campaign: Campaign): void {
  // Import / sample load / boot restore all land here: REPLACING the
  // document, not editing it → the history resets instead of recording.
  resetCampaignHistory();
  setState({ campaign: { ...campaign, updatedAt: nowIso() } });
}

export function updateCampaign(mutator: (c: Campaign) => Campaign): void {
  const previous = state.campaign;
  if (!previous) return;
  const mutated = mutator(previous);
  // Record the state JUST BEFORE the edit — and only when the mutator
  // actually changed something (a no-op must not leave an undo step that
  // appears to do nothing). Snapshots are references, not clones; the full
  // design (bound, coalescing window, why combat state is covered) lives in
  // state/history.ts. Every campaign action in this file funnels through
  // here, so this one wrap point IS the history.
  if (mutated !== previous) recordCampaignEdit(previous);
  setState({ campaign: { ...mutated, updatedAt: nowIso() } });
}

/**
 * Undo the most recent campaign edit; true when an entry was applied.
 * Only `campaign` changes — view, selection, locale and the rest of UI
 * state stay exactly where they are (history never captures them) — and
 * the swapped campaign wakes the app's existing autosave effect, so the
 * undone state is what gets persisted (storage.ts, the one path; no
 * second persistence mechanism is added here).
 */
export function undoCampaignEdit(): boolean {
  const current = state.campaign;
  if (!current) return false;
  const previous = takeUndo(current);
  if (previous === null) return false;
  // The undo IS an edit: stamped like every other change.
  setState({ campaign: { ...previous, updatedAt: nowIso() } });
  return true;
}

/** Re-apply the edit an undo took back; true when one was re-applied. */
export function redoCampaignEdit(): boolean {
  const current = state.campaign;
  if (!current) return false;
  const next = takeRedo(current);
  if (next === null) return false;
  setState({ campaign: { ...next, updatedAt: nowIso() } });
  return true;
}

export function setLocale(locale: Locale): void {
  setUiState({ locale });
}

/** Route change clears the list selection (each section selects its own). */
export function navigate(route: AppRoute): void {
  setUiState({ route, selectedId: null });
}

export function setToast(
  toastKey: string | null,
  params?: ToastParams | null,
): void {
  setUiState({ toastKey, toastParams: params ?? null });
}

export function setSaveStatus(saveStatus: SaveStatus, lastSavedAt?: string): void {
  setUiState(lastSavedAt ? { saveStatus, lastSavedAt } : { saveStatus });
}

export function clearCampaign(): void {
  resetCampaignHistory(); // dropping the document drops its history too
  setState({ campaign: null });
}

/** Non-reactive read for the persistence layer (autosave source). */
export function getCampaign(): Campaign | null {
  return state.campaign;
}

/** Test/reset helper — not used by the UI. */
export function __resetState(): void {
  resetCampaignHistory();
  state = initialState;
  for (const l of listeners) l();
}

// ----------------------------------------------------- search palette (Ctrl+K)

export function openSearch(): void {
  setUiState({ searchOpen: true });
}

export function closeSearch(): void {
  setUiState({ searchOpen: false });
}

export function selectEntity(id: string | null): void {
  setUiState({ selectedId: id });
}

// ------------------------------------------------------- GM screen pins (P6)

/**
 * Pin/unpin a GM screen panel — "this is what my table uses". The list lives
 * on the CAMPAIGN (`campaign.pinnedGmPanels`), so it rides the app's one
 * existing persistence path: the debounced localStorage autosave and the
 * .atlagm export/import — no second storage mechanism. Pinning is a toggle:
 * pinning an already-pinned panel unpins it (reversible from the panel itself).
 */
export function toggleGmPanelPin(panelId: string): void {
  updateCampaign((c) => {
    const current = c.pinnedGmPanels ?? [];
    const pinned = current.includes(panelId);
    return {
      ...c,
      pinnedGmPanels: pinned
        ? current.filter((id) => id !== panelId)
        : [...current, panelId],
    };
  });
}

// ------------------------------------------------------------- sessions (P2)

export function createSession(): Session | null {
  const campaign = state.campaign;
  if (!campaign) return null;
  const number = campaign.sessions.reduce((max, s) => Math.max(max, s.number), 0) + 1;
  const session = createEntity({
    campaignId: campaign.id,
    number,
    title: { tr: null, en: null },
    date: null,
    summary: '',
    plannedContent: '',
    linkedEncounterIds: [],
    linkedLocationIds: [],
    linkedStoryNodeIds: [],
    advancementNotes: '',
  });
  updateCampaign((c) => ({ ...c, sessions: [...c.sessions, session] }));
  setUiState({ selectedId: session.id });
  return session;
}

export function updateSession(id: string, mutator: (session: Session) => Session): void {
  updateCampaign((c) => ({
    ...c,
    sessions: c.sessions.map((s) => (s.id === id ? mutator(s) : s)),
  }));
}

export function deleteSession(id: string): void {
  updateCampaign((c) => ({ ...c, sessions: c.sessions.filter((s) => s.id !== id) }));
  clearSelectionIf(id);
}

// ---------------------------------------------------------------- npcs (P2)

export function createNpc(): Npc | null {
  const campaign = state.campaign;
  if (!campaign) return null;
  const npc = createEntity({
    campaignId: campaign.id,
    name: '',
    concept: '',
    attitude: 'neutral' as const,
    factionId: null,
    locationId: null,
    secrets: [],
    statblockRef: null,
    source: null,
  });
  updateCampaign((c) => ({ ...c, npcs: [...c.npcs, npc] }));
  setUiState({ selectedId: npc.id });
  return npc;
}

export function updateNpc(id: string, mutator: (npc: Npc) => Npc): void {
  updateCampaign((c) => ({ ...c, npcs: c.npcs.map((n) => (n.id === id ? mutator(n) : n)) }));
}

export function deleteNpc(id: string): void {
  updateCampaign((c) => ({ ...c, npcs: c.npcs.filter((n) => n.id !== id) }));
  clearSelectionIf(id);
}

// ------------------------------------------------------------- characters (P3)

/** Fate sheet per the Turkish playtest character sheet (karakter kağıdı.png). */
function defaultFateSheet(): FateSheet {
  return {
    kind: 'fate',
    aspects: [
      { label: 'high-concept', text: '' },
      { label: 'trouble', text: '' },
      { label: 'relationship', text: '' },
      { label: 'free', text: '' },
      { label: 'free', text: '' },
    ],
    skills: {},
    // The sheet says "3 stunt yazarak başlayın" — 3 editable rows from the
    // start keeps "stunt + refresh = 6" green on a fresh sheet.
    stunts: [
      { name: '', text: '', refreshCost: 1 },
      { name: '', text: '', refreshCost: 1 },
      { name: '', text: '', refreshCost: 1 },
    ],
    refresh: 3,
    fatePoints: 3,
    stress: { beden: 0, irade: 0 },
    consequences: [
      { severity: 'hafif', text: '', value: 2 },
      { severity: 'orta', text: '', value: 4 },
      { severity: 'agir', text: '', value: 6 },
    ],
    element: null,
    bending: [],
  };
}

/** D&D 5e ATLA sheet per the ATLA 5e Homebrew (lineage + class + slots). */
function defaultDnD5eSheet(era: string | null): DnD5eSheet {
  return {
    kind: 'dnd5e-atla',
    lineage: null,
    era,
    classes: [{ name: '', level: 1, specialization: null }],
    abilityScores: { str: 10, dex: 10, con: 10, int: 10, wis: 10, cha: 10 },
    techniqueSlots: [
      { tier: 'practiced', count: 0 },
      { tier: 'trained', count: 0 },
      { tier: 'mastered', count: 0 },
    ],
    techniqueIds: [],
    fightingTechniqueIds: [],
    featIds: [],
    training: [],
    gold: null,
  };
}

export function defaultSheetFor(ruleSystem: RuleSystemId, era: string | null): CharacterSheet {
  if (ruleSystem === 'fate') return defaultFateSheet();
  if (ruleSystem === 'mixed') return defaultMixedSheet(era);
  return defaultDnD5eSheet(era);
}

/**
 * The campaign the callers need, created on the spot when there is none.
 *
 * A campaign is the GM's document; a player on the public build has none and
 * must still be able to build a character. Rather than branching on
 * `campaign === null` in every view, they ask for the campaign they need.
 */
export function ensureCampaign(ruleSystem: RuleSystemId, name: string): Campaign {
  return state.campaign ?? createCampaign(name, ruleSystem);
}

export function createCharacter(): Character | null {
  // A bare "new character" still needs a document to live in; the builder
  // launcher is what provisions one (see ensureCampaign + BuilderLauncher).
  const campaign = state.campaign;
  if (!campaign) return null;
  const character = createEntity({
    campaignId: campaign.id,
    playerName: null,
    name: '',
    concept: '',
    sheet: defaultSheetFor(campaign.ruleSystem, campaign.era),
    portraitPath: null,
    campaignNotes: '',
  });
  updateCampaign((c) => ({ ...c, characters: [...c.characters, character] }));
  setUiState({ selectedId: character.id });
  return character;
}

/**
 * Append a whole IMPORTED batch (the character transfer panel's multi-file /
 * multi-code import) in ONE mutation. The characters arrive already parsed and
 * stamped with fresh ids by `adoptCharacter`, so this is a single
 * `updateCampaign`: one history entry (Ctrl+Z takes the batch back as a unit —
 * never one character at a time) and one debounced autosave for the lot,
 * instead of N undo steps and N writes. An empty batch writes NOTHING (a
 * no-op must not leave an undo step that appears to do nothing).
 */
export function appendImportedCharacters(characters: Character[]): void {
  if (characters.length === 0) return;
  updateCampaign((c) => ({ ...c, characters: [...c.characters, ...characters] }));
}

export function updateCharacter(id: string, mutator: (ch: Character) => Character): void {
  updateCampaign((c) => ({
    ...c,
    characters: c.characters.map((ch) => (ch.id === id ? mutator(ch) : ch)),
  }));
}

/**
 * THE character HP writer — the Party card and the combat tracker both call
 * it, so there is exactly one path to `sheet.hp` (Fate sheets have no hit
 * points and are left untouched).
 */
export function writeCharacterHp(characterId: string, hp: Hp): void {
  updateCharacter(characterId, (ch) => {
    const sheet = ch.sheet;
    if (sheet.kind === 'mixed' || sheet.kind === 'dnd5e-atla') {
      return { ...ch, sheet: { ...sheet, hp } };
    }
    return ch;
  });
}

export function deleteCharacter(id: string): void {
  updateCampaign((c) => ({ ...c, characters: c.characters.filter((ch) => ch.id !== id) }));
  clearSelectionIf(id);
}

// --------------------------------------------------- advancement (Fate / 5e)

/**
 * The result of an advancement request: `ok: false` carries the refusal's
 * i18n key + params (+ the p.40 "skills to raise first" list). A refusal
 * happens BEFORE any mutation — no setState, no `updatedAt`, no history
 * entry, nothing written.
 */
export type StepWriteResult = { ok: true } | { ok: false; refusal: Refusal };

const NOT_FOUND: Refusal = {
  code: 'not-found',
  key: 'advance.refuse.not-found',
  params: {},
  raiseFirst: [],
  violations: [],
};

function findCharacter(id: string): Character | null {
  return state.campaign?.characters.find((ch) => ch.id === id) ?? null;
}

/**
 * The ONE write path for a step: sheet + advancement record move together in
 * a single `updateCampaign`, so undo/redo, autosave and the `.atlagm`
 * round-trip always see the same document.
 */
function writeStep(characterId: string, outcome: AdvancementOutcome): void {
  const at = nowIso();
  const entry: AdvancementEntry = { id: newId(), at, ...outcome.entry };
  updateCampaign((c) => ({
    ...c,
    characters: c.characters.map((ch) => {
      if (ch.id !== characterId) return ch;
      const previous = ch.advancement;
      const advancement: AdvancementState = {
        points: outcome.points,
        log: [...(previous?.log ?? []), entry],
      };
      return { ...ch, sheet: outcome.sheet, advancement };
    }),
  }));
}

/**
 * The advancement rules live in `@/domain/advance` — a LAZY chunk, so the entry
 * chunk does not carry a feature nobody has opened yet (the `check:bundle`
 * budget: entry ≤ 200 kB raw / ≤ 70 kB gzip).
 *
 * The import is awaited FIRST; reading the character, validating the request
 * and writing the outcome then happen in one synchronous stretch. A refusal
 * therefore still returns before any mutation: no `setState`, no `updatedAt`,
 * no history entry.
 */
type AdvanceRules = typeof import('@/domain/advance');

async function advanceRules(): Promise<AdvanceRules> {
  return import('@/domain/advance');
}

/** KÜÇÜK ADIM — exactly one minor option (p.39), or the request is refused. */
export async function applyMinorStep(
  characterId: string,
  options: FateMinorOption[],
): Promise<StepWriteResult> {
  const { takeMinorStep } = await advanceRules();
  const character = findCharacter(characterId);
  if (!character) return { ok: false, refusal: NOT_FOUND };
  const result = takeMinorStep(character, options);
  if (!result.ok) return { ok: false, refusal: result.refusal };
  writeStep(characterId, result.outcome);
  return { ok: true };
}

/** BÜYÜK ADIM — one minor option + the mandatory set (p.39), or refused. */
export async function applyMajorStep(
  characterId: string,
  request: MajorStepRequest,
): Promise<StepWriteResult> {
  const { takeMajorStep } = await advanceRules();
  const character = findCharacter(characterId);
  if (!character) return { ok: false, refusal: NOT_FOUND };
  const result = takeMajorStep(character, request);
  if (!result.ok) return { ok: false, refusal: result.refusal };
  writeStep(characterId, result.outcome);
  return { ok: true };
}

/** 5e / Mixed level change — re-derived, never a new table (p.7). */
export async function applyLevelChange(characterId: string, level: number): Promise<StepWriteResult> {
  const { takeLevelChange } = await advanceRules();
  const character = findCharacter(characterId);
  if (!character) return { ok: false, refusal: NOT_FOUND };
  const result = takeLevelChange(character, level);
  if (!result.ok) return { ok: false, refusal: result.refusal };
  writeStep(characterId, result.outcome);
  return { ok: true };
}

// --------------------------------------------------------------- notes (P2)

export function createNote(): Note | null {
  const campaign = state.campaign;
  if (!campaign) return null;
  const note = createEntity({
    campaignId: campaign.id,
    title: '',
    body: '',
    tags: [],
    links: [],
  });
  updateCampaign((c) => ({ ...c, notes: [...c.notes, note] }));
  setUiState({ selectedId: note.id });
  return note;
}

export function updateNote(id: string, mutator: (note: Note) => Note): void {
  updateCampaign((c) => ({ ...c, notes: c.notes.map((n) => (n.id === id ? mutator(n) : n)) }));
}

export function deleteNote(id: string): void {
  updateCampaign((c) => ({ ...c, notes: c.notes.filter((n) => n.id !== id) }));
  clearSelectionIf(id);
}

// ----------------------------------------------------------- encounters (P4)

export function createEncounter(): Encounter | null {
  const campaign = state.campaign;
  if (!campaign) return null;
  const encounter = createEntity<Omit<Encounter, 'id' | 'createdAt' | 'updatedAt'>>({
    campaignId: campaign.id,
    name: '',
    purpose: '',
    combatStyle: campaign.ruleSystem === 'fate' ? 'zones' : 'initiative',
    participants: [],
    obstacles: [],
    rewardNotes: '',
    status: 'planned',
  });
  updateCampaign((c) => ({ ...c, encounters: [...c.encounters, encounter] }));
  setUiState({ selectedId: encounter.id });
  return encounter;
}

export function updateEncounter(id: string, mutator: (encounter: Encounter) => Encounter): void {
  updateCampaign((c) => ({
    ...c,
    encounters: c.encounters.map((e) => (e.id === id ? mutator(e) : e)),
  }));
}

export function deleteEncounter(id: string): void {
  updateCampaign((c) => ({ ...c, encounters: c.encounters.filter((e) => e.id !== id) }));
  clearSelectionIf(id);
}

// ------------------------------------------------------------- enemies (P6)

export function createEnemy(): Enemy | null {
  const campaign = state.campaign;
  if (!campaign) return null;
  const enemy = createEntity<Omit<Enemy, 'id' | 'createdAt' | 'updatedAt'>>({
    campaignId: campaign.id,
    name: '',
    role: '',
    statblock: '',
    source: null,
  });
  updateCampaign((c) => ({ ...c, enemies: [...c.enemies, enemy] }));
  setUiState({ selectedId: enemy.id });
  return enemy;
}

export function updateEnemy(id: string, mutator: (enemy: Enemy) => Enemy): void {
  updateCampaign((c) => ({ ...c, enemies: c.enemies.map((e) => (e.id === id ? mutator(e) : e)) }));
}

export function deleteEnemy(id: string): void {
  updateCampaign((c) => ({ ...c, enemies: c.enemies.filter((e) => e.id !== id) }));
  clearSelectionIf(id);
}

// ------------------------------------------------------------ locations (P5)

export function createLocation(): Location | null {
  const campaign = state.campaign;
  if (!campaign) return null;
  const location = createEntity<Omit<Location, 'id' | 'createdAt' | 'updatedAt'>>({
    campaignId: campaign.id,
    name: '',
    parentId: null,
    kind: '',
    description: '',
    mapId: null,
    factionIds: [],
    notes: '',
  });
  updateCampaign((c) => ({ ...c, locations: [...c.locations, location] }));
  setUiState({ selectedId: location.id });
  return location;
}

export function updateLocation(id: string, mutator: (location: Location) => Location): void {
  updateCampaign((c) => ({
    ...c,
    locations: c.locations.map((l) => (l.id === id ? mutator(l) : l)),
  }));
}

export function deleteLocation(id: string): void {
  updateCampaign((c) => ({ ...c, locations: c.locations.filter((l) => l.id !== id) }));
  clearSelectionIf(id);
}

// ----------------------------------------------------------------- maps (P5)

export function createMap(name: string = ''): GameMap | null {
  const campaign = state.campaign;
  if (!campaign) return null;
  const map = createEntity<Omit<GameMap, 'id' | 'createdAt' | 'updatedAt'>>({
    campaignId: campaign.id,
    name: name || (state.ui.locale === 'tr' ? 'Yeni Harita' : 'New Map'),
    imageAssetId: null,
    width: 1000,
    height: 700,
    markers: [],
  });
  updateCampaign((c) => ({ ...c, maps: [...c.maps, map] }));
  return map;
}

export function updateMap(id: string, mutator: (map: GameMap) => GameMap): void {
  updateCampaign((c) => ({
    ...c,
    maps: c.maps.map((m) => (m.id === id ? mutator(m) : m)),
  }));
}

export function deleteMap(id: string): void {
  updateCampaign((c) => ({ ...c, maps: c.maps.filter((m) => m.id !== id) }));
}

export function addMapMarker(mapId: string, marker: Omit<MapMarker, 'id'>): void {
  const markerWithId: MapMarker = {
    ...marker,
    id: `marker-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`,
  };
  updateMap(mapId, (m) => ({ ...m, markers: [...m.markers, markerWithId] }));
}

export function updateMapMarker(mapId: string, markerId: string, patch: Partial<MapMarker>): void {
  updateMap(mapId, (m) => ({
    ...m,
    markers: m.markers.map((mk) => (mk.id === markerId ? { ...mk, ...patch } : mk)),
  }));
}

export function deleteMapMarker(mapId: string, markerId: string): void {
  updateMap(mapId, (m) => ({
    ...m,
    markers: m.markers.filter((mk) => mk.id !== markerId),
  }));
}

/**
 * Figures on the battle grid (`GameMap.tokens`) — the SAME mutation path as
 * the markers and everything else the campaign edits: `updateMap` →
 * `updateCampaign`, so autosave, undo/redo (`state/history.ts`) and the
 * `.atlagm` round-trip cover every token edit with no second persistence
 * path. Coordinates arrive as square centres (the view snaps through
 * `snapPointToSquare`); the id is minted here, like a marker's.
 *
 * The optional `ref` (the initiative-tracker link) rides the SAME path —
 * `addMapToken` stores it when the GM adds a roster figure, and
 * `updateMapToken`'s `Partial<MapToken>` patch links, relinks (`{ ref }`)
 * or clears it (`{ ref: undefined }` → dropped by JSON.stringify). No new
 * action, no second path: undo/redo and autosave cover linking too, and a
 * campaign whose figures carry no `ref` loads unchanged.
 */
export function addMapToken(mapId: string, token: Omit<MapToken, 'id'>): void {
  const tokenWithId: MapToken = {
    ...token,
    id: `token-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`,
  };
  updateMap(mapId, (m) => ({ ...m, tokens: [...(m.tokens ?? []), tokenWithId] }));
}

export function updateMapToken(mapId: string, tokenId: string, patch: Partial<MapToken>): void {
  updateMap(mapId, (m) => ({
    ...m,
    tokens: (m.tokens ?? []).map((tok) => (tok.id === tokenId ? { ...tok, ...patch } : tok)),
  }));
}

export function deleteMapToken(mapId: string, tokenId: string): void {
  updateMap(mapId, (m) => ({
    ...m,
    tokens: (m.tokens ?? []).filter((tok) => tok.id !== tokenId),
  }));
}

// ------------------------------------------------------------- factions (P5)

export function createFaction(): Faction | null {
  const campaign = state.campaign;
  if (!campaign) return null;
  const faction = createEntity<Omit<Faction, 'id' | 'createdAt' | 'updatedAt'>>({
    campaignId: campaign.id,
    name: '',
    goal: '',
    influence: '',
    locationIds: [],
    notableMembers: [],
    notes: '',
  });
  updateCampaign((c) => ({ ...c, factions: [...c.factions, faction] }));
  setUiState({ selectedId: faction.id });
  return faction;
}

export function updateFaction(id: string, mutator: (faction: Faction) => Faction): void {
  updateCampaign((c) => ({
    ...c,
    factions: c.factions.map((f) => (f.id === id ? mutator(f) : f)),
  }));
}

export function deleteFaction(id: string): void {
  updateCampaign((c) => ({ ...c, factions: c.factions.filter((f) => f.id !== id) }));
  clearSelectionIf(id);
}

// ----------------------------------------------------------- storyNodes (P5)

export function createStoryNode(): StoryNode | null {
  const campaign = state.campaign;
  if (!campaign) return null;
  const storyNode = createEntity<Omit<StoryNode, 'id' | 'createdAt' | 'updatedAt'>>({
    campaignId: campaign.id,
    title: '',
    summary: '',
    status: 'seeded',
    edges: [],
    linkedLocationIds: [],
  });
  updateCampaign((c) => ({ ...c, storyNodes: [...c.storyNodes, storyNode] }));
  setUiState({ selectedId: storyNode.id });
  return storyNode;
}

export function updateStoryNode(id: string, mutator: (node: StoryNode) => StoryNode): void {
  updateCampaign((c) => ({
    ...c,
    storyNodes: c.storyNodes.map((n) => (n.id === id ? mutator(n) : n)),
  }));
}

export function deleteStoryNode(id: string): void {
  updateCampaign((c) => ({ ...c, storyNodes: c.storyNodes.filter((n) => n.id !== id) }));
  clearSelectionIf(id);
}

function clearSelectionIf(id: string): void {
  if (state.ui.selectedId === id) setUiState({ selectedId: null });
}

// ------------------------------------------------------------------- hooks

/**
 * Observe every state change from OUTSIDE React — the hash router writes the
 * route back to `location.hash` through this (ui/router.ts), and the gates
 * drive it directly. Same listener set the React hook subscribes to; returns
 * the unbind function.
 */
export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

const getSnapshot = (): AppState => state;

/** Whole-state subscription; snapshot identity is stable between updates. */
export function useAppState(): AppState {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
